import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { Modal } from '@/components/Modal'
import { AlertModal, AlertType } from '@/components/AlertModal'
import FeatureGate from '@/components/FeatureGate'
import Pagination, { paginate } from '@/components/Pagination'
import { Key, Copy, Check, Trash2, Plus } from 'lucide-react'

interface OAuthClient {
  oauth_client_id: number
  client_id: string
  client_secret_prefix: string
  name: string
  description?: string
  status: string
  created_at: string
}

interface CreatedCredentials {
  client_id: string
  client_secret: string
  api_key: string
}

export default function OAuthClientsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role?.toUpperCase() || '')
  const queryClient = useQueryClient()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [credentials, setCredentials] = useState<CreatedCredentials | null>(null)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [clientPage, setClientPage] = useState(1)
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
  }>({ isOpen: false, type: 'info', title: '', message: '' })

  const { data: clientsData, isLoading } = useQuery({
    queryKey: ['oauth-clients', vendorId],
    queryFn: () => api.vendors.oauthClients.list(vendorId!.toString()),
    enabled: !!vendorId,
  })

  const clients: OAuthClient[] = (clientsData as any)?.data?.data || []

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.vendors.oauthClients.create(vendorId!.toString(), data),
    onSuccess: (response: any) => {
      const data = response?.data?.data
      if (data) {
        setCredentials({
          client_id: data.client_id,
          client_secret: data.client_secret,
          api_key: data.api_key,
        })
      }
      setShowCreateModal(false)
      setCreateName('')
      setCreateDescription('')
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || 'Failed to create OAuth client',
      })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) =>
      api.vendors.oauthClients.revoke(vendorId!.toString(), clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] })
    },
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleRevoke = (client: OAuthClient) => {
    setAlertModal({
      isOpen: true,
      type: 'warning',
      title: 'Revoke OAuth Client',
      message: `This will permanently revoke "${client.name}" and its associated API key. Any apps using these credentials will stop working.`,
      onConfirm: () => {
        revokeMutation.mutate(client.client_id)
        setAlertModal(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  const pageContent = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>
            OAuth Clients
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
            Connect your Tixxly account to AI platforms like ChatGPT, Claude, and others.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1rem', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)',
            border: 'none', borderRadius: '6px', cursor: 'pointer',
          }}
        >
          <Plus size={16} /> Create Client
        </button>
      </div>

      {/* Setup instructions */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: 0, lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--color-gray-700)' }}>How it works:</strong> Create an OAuth client to get a Client ID and Client Secret.
          Use these credentials when setting up your app on ChatGPT, Claude, or any AI platform that supports MCP.
          Set the Authorization URL to <code style={{ fontSize: 'var(--text-xs)', background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '4px' }}>https://mcp.tixxly.ai/oauth/authorize</code> and
          Token URL to <code style={{ fontSize: 'var(--text-xs)', background: 'var(--color-gray-100)', padding: '2px 6px', borderRadius: '4px' }}>https://mcp.tixxly.ai/oauth/token</code>.
        </p>
      </div>

      {/* Client list */}
      {isLoading ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}>
          Loading...
        </div>
      ) : clients.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <Key size={40} style={{ color: 'var(--color-gray-300)', marginBottom: '1rem' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
            No OAuth clients yet. Create one to start integrating with AI platforms.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {paginate(clients, clientPage).map(client => (
            <div key={client.oauth_client_id} className="card" style={{ padding: '1rem 1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>
                      {client.name}
                    </span>
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: client.status === 'active' ? 'var(--color-lime-green-light, #ecfccb)' : 'var(--color-gray-100)',
                      color: client.status === 'active' ? 'var(--color-lime-green-dark, #365314)' : 'var(--color-gray-500)',
                    }}>
                      {client.status}
                    </span>
                  </div>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', fontFamily: 'monospace', margin: '0.25rem 0' }}>
                    {client.client_id}
                  </p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', margin: 0 }}>
                    Secret: {client.client_secret_prefix}...  |  Created {new Date(client.created_at).toLocaleDateString()}
                  </p>
                  {client.description && (
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: '0.25rem 0 0' }}>
                      {client.description}
                    </p>
                  )}
                </div>
                {client.status === 'active' && (
                  <button
                    onClick={() => handleRevoke(client)}
                    disabled={revokeMutation.isPending}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.25rem',
                      padding: '0.375rem 0.75rem', fontSize: 'var(--text-xs)',
                      color: 'var(--color-coral-red)', background: 'transparent',
                      border: '1px solid var(--color-coral-red)', borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={12} /> Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Pagination page={clientPage} totalItems={clients.length} onPageChange={setClientPage} />

      {/* Create modal */}
      {showCreateModal && (
        <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create OAuth Client">
          <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate({ name: createName, description: createDescription || undefined }) }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>
                Name
              </label>
              <input
                type="text"
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                placeholder="e.g. ChatGPT App"
                required
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)',
                  border: '1px solid var(--color-gray-200)', borderRadius: '6px',
                }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={createDescription}
                onChange={e => setCreateDescription(e.target.value)}
                placeholder="What is this client for?"
                style={{
                  width: '100%', padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)',
                  border: '1px solid var(--color-gray-200)', borderRadius: '6px',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateModal(false)}
                style={{ padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', background: 'var(--color-gray-100)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={!createName || createMutation.isPending}
                style={{
                  padding: '0.5rem 1rem', fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  opacity: !createName || createMutation.isPending ? 0.5 : 1,
                }}>
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Credentials modal (shown once after creation) */}
      {credentials && (
        <Modal isOpen={!!credentials} onClose={() => setCredentials(null)} title="OAuth Client Created">
          <div style={{ marginBottom: '1rem' }}>
            <p style={{
              fontSize: 'var(--text-sm)', color: 'var(--color-coral-red)',
              fontWeight: 'var(--font-weight-semibold)', marginBottom: '1rem',
              padding: '0.75rem', background: '#fef2f2', borderRadius: '6px',
            }}>
              Copy these credentials now. The Client Secret and API Key will not be shown again.
            </p>

            <CredentialField label="Client ID" value={credentials.client_id} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="Client Secret" value={credentials.client_secret} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="MCP API Key" value={credentials.api_key} copied={copied} onCopy={copyToClipboard} />
          </div>

          <div style={{ padding: '0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px', marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: 0, lineHeight: 1.6 }}>
              <strong>ChatGPT setup:</strong> Go to your app settings, set Authentication to OAuth, and enter:<br />
              Authorization URL: <code>https://mcp.tixxly.ai/oauth/authorize</code><br />
              Token URL: <code>https://mcp.tixxly.ai/oauth/token</code><br />
              Client ID and Client Secret from above.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setCredentials(null)}
              style={{
                padding: '0.5rem 1.5rem', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
              }}>
              Done
            </button>
          </div>
        </Modal>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={alertModal.onConfirm}
        confirmText="Revoke"
        cancelText="Cancel"
      />
    </>
  )

  // Admins bypass tier gate
  if (isAdmin) {
    return <div>{pageContent}</div>
  }

  return (
    <div>
      <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 1.5rem' }}>
        OAuth Clients
      </h2>
      <FeatureGate
        requiredTier={1}
        title="OAuth Clients for AI Platforms"
        description="Upgrade to Growth ($99/mo) to create OAuth clients and connect your experiences to ChatGPT, Claude, and other AI platforms."
      >
        {pageContent}
      </FeatureGate>
    </div>
  )
}

function CredentialField({ label, value, copied, onCopy }: {
  label: string
  value: string
  copied: string | null
  onCopy: (text: string, label: string) => void
}) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.25rem' }}>
        {label}
      </label>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.5rem 0.75rem', background: 'var(--color-gray-50)',
        borderRadius: '6px', border: '1px solid var(--color-gray-200)',
      }}>
        <code style={{ flex: 1, fontSize: 'var(--text-xs)', wordBreak: 'break-all', color: 'var(--color-gray-700)' }}>
          {value}
        </code>
        <button
          onClick={() => onCopy(value, label)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)' }}
          title="Copy"
        >
          {copied === label ? <Check size={14} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  )
}
